/**
 * Internal System-Flag API Contract (registry-backed)
 *
 * GET  /internal/config/flags               — list the registered flags
 * GET  /internal/config/flags/:name         — read one flag's resolved state
 * POST /internal/config/flags/:name         — write one flag
 * DELETE /internal/config/flags/:name?school_id= — remove one school's override row
 *
 * API-key-gated (`INTERNAL_API_KEY`). These endpoints are the operator control
 * surface for `SystemConfiguration` feature flags. They are deliberately NOT a
 * free-form `{category, key, value}` writer: the backend REGISTRY is the
 * allowlist (an unregistered `:name` is a 404, never a new row) and the type
 * boundary (a value that fails the registry's Zod schema is a 400, never a
 * malformed row a fail-closed resolver silently ignores).
 *
 * SCOPE. `school_id` is OPTIONAL on both the read and the write: omit it for the
 * FLEET-WIDE row (`school_id IS NULL`); supply it for that school's override.
 * A supplied-but-unknown school is a 404 — never a silent fleet-wide write.
 *
 * AUDIT. Every write records `{ reason, changedBy, changedAt }` in the row's
 * `metadata` column, separate from the functional `value` the resolver reads.
 * The read surfaces it back.
 *
 * Entity shapes (`SystemFlagState`, `SystemFlagDescriptor`, `SystemFlagAudit`)
 * live in `schemas/entities/system-flag`.
 */

import { z } from 'zod'
import {
  SystemFlagAuditRequestFields,
  SystemFlagAuditSchema,
  SystemFlagDescriptorSchema,
  SystemFlagStateSchema
} from '../../schemas/entities/system-flag'

/**
 * GET /internal/config/flags — response. Lists every registered flag so an
 * operator can discover what is settable without reading backend source.
 */
export const ListSystemFlagsResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    flags: z.array(SystemFlagDescriptorSchema)
  })
})

export type ListSystemFlagsResponse = z.infer<typeof ListSystemFlagsResponseSchema>

/**
 * GET /internal/config/flags/:name — query params.
 */
export const GetSystemFlagRequestSchema = z.object({
  name: z.string().min(1).describe('Registered flag name from the path'),
  school_id: z
    .string()
    .min(1)
    .optional()
    .describe('OPTIONAL school public ID (CUID) or slug; omit to read the fleet-wide row alone')
})

export type GetSystemFlagRequest = z.infer<typeof GetSystemFlagRequestSchema>

/**
 * GET /internal/config/flags/:name — response.
 */
export const GetSystemFlagResponseSchema = z.object({
  message: z.string(),
  data: SystemFlagStateSchema
})

export type GetSystemFlagResponse = z.infer<typeof GetSystemFlagResponseSchema>

/**
 * POST /internal/config/flags/:name — request body.
 *
 * `value` is validated against the REGISTRY's schema for `name`, not here — this
 * contract only guarantees it is a JSON object. Every registered flag requires
 * its COMPLETE value object (no partial writes): the write replaces the target
 * row's `value` wholesale, so accepting a partial would let an operator flipping
 * one key silently clear the others.
 */
export const SetSystemFlagRequestSchema = z.object({
  name: z.string().min(1).describe('Registered flag name from the path'),
  school_id: z
    .string()
    .min(1)
    .optional()
    .describe('OPTIONAL school public ID (CUID) or slug; omit to write the fleet-wide row'),
  value: z.record(z.string(), z.unknown()).describe("The flag's complete value object; shape is per-flag"),
  ...SystemFlagAuditRequestFields
})

export type SetSystemFlagRequest = z.infer<typeof SetSystemFlagRequestSchema>

/**
 * POST /internal/config/flags/:name — response. Returns the post-write resolved
 * state, so the caller sees the truth including school-over-system precedence
 * rather than an echo of what it sent.
 */
export const SetSystemFlagResponseSchema = z.object({
  message: z.string(),
  data: SystemFlagStateSchema
})

export type SetSystemFlagResponse = z.infer<typeof SetSystemFlagResponseSchema>

/**
 * DELETE /internal/config/flags/:name?school_id= — query params.
 *
 * `school_id` is REQUIRED, unlike the read and the write: the delete removes ONE
 * school's override row, after which that school inherits the fleet-wide value
 * again. The fleet-wide row is never deletable through this route — it is the
 * base every school inherits, and changing it is a write (`POST`) with the
 * complete intended value, not a removal.
 */
export const DeleteSystemFlagRequestSchema = z.object({
  name: z.string().min(1).describe('Registered flag name from the path'),
  school_id: z.string().min(1).describe('REQUIRED school public ID (CUID) or slug whose override row is removed')
})

export type DeleteSystemFlagRequest = z.infer<typeof DeleteSystemFlagRequestSchema>

/**
 * DELETE /internal/config/flags/:name — response. Echoes the row that was
 * removed, or `found: false` (and `removed: null`) when the school had no row —
 * a 200 either way, so a retried delete is not an error.
 */
export const DeleteSystemFlagResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    name: z.string().describe('The registered flag name'),
    category: z.string().describe('`SystemConfiguration.category` the row was stored under'),
    key: z.string().describe('`SystemConfiguration.key` the row was stored under'),
    schoolId: z.string().describe("The school's public ID"),
    found: z.boolean().describe('Whether the school had an override row to remove'),
    removed: z
      .object({
        value: z.unknown().nullable().describe('The removed row value'),
        audit: SystemFlagAuditSchema.nullable().describe('The removed row audit trail, if it had one')
      })
      .nullable()
      .describe('The row that was removed; null when none existed')
  })
})

export type DeleteSystemFlagResponse = z.infer<typeof DeleteSystemFlagResponseSchema>
